import {Component, EventEmitter, Inject, Input, Output, OnInit} from 'ng-metadata/core';
import './menu-item.component.scss';

/**
 * @ngdoc directive
 * @name mwMenuItem
 * @module mw.components.menu
 *
 * @param {string} [aria-label] - The aria-label that should be applied to the menu item button wrapper.
 * @param {boolean} [auto-height=false] - Allow the height of the menu item to be determined by its' contents.
 * @param {string} [hover-label] - The hover label that should display when the menu item is hovered over.
 * @param {boolean} [detail-label] - The detail label that should display always.
 * @param {string} [icon] - Name of an icon to be added to the menu item.  Should be in the same format supported by the 'md-svg-icon' param of an `<md-icon>`
 * @param {boolean} [is-disabled=false] - This value determines if the menu item should be displayed in a disabled state.
 * @param {boolean} [is-indeterminate=false] - When the 'type' of the menu item is 'multiselect', this value will determine the indeterminate state.
 * @param {boolean} [is-selected=false] - When the 'type' of the menu item is 'singleselect' or 'multiselect', this value will determine the selected state.
 * @param {expression} [on-click] - Angular expression to evaluate on click of the menu item. $event is the ng-click event.
 * @param {string} [type='simple'] - Supported values include 'simple', 'singleselect', 'multiselect', and 'focusdummy'.
 * @param {string} [url] - If provided, menu item will be treated like an href when clicked.
 * @param {boolean} [show-icon-on-hover=false] - Enables to show icon on hover event. Use only when using list items.
 * @param {boolean} [is-editable=false] - EXPERIMENTAL: Enables edit behavior.
 * @param {boolean} [is-removable=false] - EXPERIMENTAL: Enables remove behavior.
 * @param {boolean} [edit-mode] - EXPERIMENTAL: Model to be know the current edit mode.
 * @param {string} [edit-model] - EXPERIMENTAL: Model to be overridden when the value changes.
 * @param {boolean} [prevent-menu-close] - Will prevent the parent md-menu from closing when clicking the menu-item or any of it's children.
 * @param {expression} [on-remove] - EXPERIMENTAL: Angular expression that triggers when the remove button is clicked
 * @param {expression} [on-edit] - EXPERIMENTAL: Angular expression that triggers when the edit button is clicked
 *
 * @description
 * `<mw-menu-item>` is a component that renders a menu item according to the Meltwater UX guidelines.  It is a flexible component that can be used to compose menus in many different contexts such as dropdowns, dialogs, or inline cards.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <!-- Simple Menu Item -->
 *   <mw-menu-item on-click="$ctrl.doSomethingCool()">Item One</mw-menu-item>
 *
 *   <!-- Simple Menu Item w/ icon -->
 *   <mw-menu-item icon="maps:ic_local_pizza_24px" on-click="$ctrl.orderPizza()">Order Pizza</mw-menu-item>
 *
 *   <!-- Simple Menu Item w/ hover -->
 *   <mw-menu-item hover-label="Order Pizza" on-click="$ctrl.orderPizza()">Order Pizza</mw-menu-item>
 *
 *   <!-- Single Select Item -->
 *   <mw-menu-item type="singleselect" is-selected="true">Item One (selected)</mw-menu-item>
 *   <mw-menu-item type="singleselect" is-selected="false">Item Two (not selected)</mw-menu-item>
 *
 *   <!-- Multi Select Item -->
 *   <mw-menu-item type="multiselect" is-selected="true">Item One (selected)</mw-menu-item>
 *   <mw-menu-item type="multiselect" is-selected="false">Item Two (not selected)</mw-menu-item>
 *   <mw-menu-item type="multiselect" is-indeterminate="true">Item Three (indeterminate)</mw-menu-item>
 *
 *   <!-- Multi Select Item w/ clickable drilldown icon -->
 *   <mw-menu-item type="multiselect" is-selected="false">
 *      Drilldown Item
 *      <!-- The mw-menu-item allows you to translude in actionable icons via the mw-menu-item-icons slot -->
 *      <mw-menu-item-icons>
 *          <md-button ng-click="$ctrl.handleDrilldown()" class="md-icon-button" >
 *              <md-icon class="mw-menu-item__drilldown-icon" md-svg-icon="navigation:ic_arrow_drop_down_24px"></md-icon>
 *          </md-button>
 *      </mw-menu-item-icons>
 *   </mw-menu-item>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-menu-item',
    template: `
        <md-divider class="mw-menu-divider" ng-if="::$ctrl.type === 'divider'"></md-divider>
        <md-list-item ng-if="::$ctrl.type !== 'divider'"
            ng-disabled="$ctrl.isDisabled"
            ng-click="::$ctrl.handleClick($event)"
            aria-label="{{::$ctrl.ariaLabel}}"
            ng-keydown="::$ctrl.handleKeyPress($event)"
            class="mw-menu-item"
            ng-class="{'mw-menu-item--selected': $ctrl.isSelected, 'mw-menu-item--auto-height': $ctrl.autoHeight, 'mw-menu-item--noicon': !$ctrl.hasIcon(), 'mw-menu-item--focusdummy': $ctrl.type === 'focusdummy', 'mw-menu-item--simple': $ctrl.type === 'simple', 'mw-menu-item--navselect': $ctrl.type === 'navselect', 'mw-menu-item--toggle-icon': $ctrl.showIconOnHover, 'mw-menu-item--is-editable':$ctrl.isEditable, 'mw-menu-item--is-editing': $ctrl.editMode, 'mw-menu-item--multiline':$ctrl.isMultiline}"
            tabindex="1"
            md-no-ink>
            <div class="mw-menu-item__checkbox-container" ng-if="::$ctrl.type === 'multiselect'">
                <md-checkbox ng-disabled="$ctrl.isDisabled" ng-click="::$ctrl.handleClick($event)" class="mw-menu-item__checkbox md-primary" md-indeterminate="$ctrl.isIndeterminate === true" ng-checked="$ctrl.isSelected" aria-label="$ctrl.ariaLabel" tabindex="-1" ng-attr-md-prevent-menu-close="{{$ctrl.preventMenuClose ? '' : undefined}}"></md-checkbox>
            </div>
            <div class="mw-menu-item__single-select-icon" ng-if="::$ctrl.type === 'singleselect'" >
                <mw-icon svg-icon="navigation:ic_check_24px" aria-label="checked" size="sm"></mw-icon>
            </div>
            <div class="mw-menu-item__avatar">
                <ng-transclude  ng-transclude-slot="sourceAvatarSlot"></ng-transclude>
            </div>
            <div class="mw-menu-item__label" ng-show="!$ctrl.editMode">
                <ng-transclude></ng-transclude>
            </div>
            <div class="mw-menu-item__hover-label" ng-if="$ctrl.hoverLabel && !$ctrl.isEditable">
                {{$ctrl.hoverLabel}}
            </div>
            <div class="mw-menu-item__detail-label" ng-if="$ctrl.detailLabel && !$ctrl.isEditable">
                {{$ctrl.detailLabel}}
            </div>
            <ng-transclude class="md-secondary mw-menu-item__icons" ng-transclude-slot="iconsSlot" ng-if="!$ctrl.isEditable">
                <mw-icon svg-icon="{{::$ctrl.icon}}" ng-if="::$ctrl.icon" size="sm"></mw-icon>
            </ng-transclude>
            <div class="mw-menu-item__edit-input" ng-if="$ctrl.isEditable" ng-show="$ctrl.editMode" flex md-truncate>
                <md-input-container>
                    <ng-transclude ng-transclude-slot="editableInput">
                        <input ng-model="$ctrl.editModel" ng-blur="$ctrl.handleCancelClick($event)" maxlength="50" mw-focus-when="$ctrl.editMode" mw-select-on-focus="$ctrl.editMode" aria-label="{{$ctrl.editModel}}">
                    </ng-transclude>
                </md-input-container>
            </div>
            <div class="mw-menu-item__edit-actions" ng-if="$ctrl.isEditable || $ctrl.isRemovable" layout="row" layout-align="end center">
                <mw-icon-button svg-icon="meltwater:edit" hover="true" size="sm" ng-click="$ctrl.handleEditClick($event)" ng-show="!$ctrl.editMode" aria-label="{{'Edit' | translate}}" ng-attr-md-prevent-menu-close="{{$ctrl.preventMenuClose ? '' : undefined}}"></mw-icon-button>
                <mw-icon-button svg-icon="action:ic_delete_24px" hover="true" size="sm" ng-click="$ctrl.handleRemoveClick($event)" ng-show="!$ctrl.editMode" aria-label="{{'Delete' | translate}}" ng-attr-md-prevent-menu-close="{{$ctrl.preventMenuClose ? '' : undefined}}"></mw-icon-button>
                <mw-icon-button svg-icon="navigation:ic_close_24px" hover="true" size="sm" ng-click="$ctrl.handleCancelClick($event)" ng-show="$ctrl.editMode && $ctrl.isRemovable" aria-label="{{'Cancel' | translate}}" ng-attr-md-prevent-menu-close="{{$ctrl.preventMenuClose ? '' : undefined}}"></mw-icon-button>
            </div>
        </md-list-item>
    `,
    legacy: {
        transclude: {
            'iconsSlot': '?mwMenuItemIcons',
            'actionsSlot': '?mwMenuItemActions',
            'editableInput': '?input',
            'sourceAvatarSlot': '?mwSourceAvatar',
        }
    }
})
export class MenuItemComponent implements OnInit {
    @Input('@') ariaLabel: string;
    @Input('@') hoverLabel: string;
    @Input('@') detailLabel: string;
    @Input('<') autoHeight: boolean = false;
    @Input('@') icon: string;
    @Input('<') isMultiline: boolean = false;
    @Input('<') isDisabled: boolean = false;
    @Input('<') isIndeterminate: boolean = false;
    @Input('<') isSelected: boolean = false;
    @Input('@') type: string = 'simple'; // Supported values 'simple'(default), 'singleselect', 'multiselect', 'navselect', 'focusdummy',
    @Input('@') url: string;
    @Input('<') openNewTab: boolean = false;
    @Input('<') showIconOnHover: boolean = false;
    @Input('<') isEditable: boolean = false;
    @Input('<') isRemovable: boolean = false;
    @Input('=') editMode: boolean = false;
    @Input('=') editModel: string = '';
    @Input('<') preventMenuClose: boolean = false;
    @Output() onClick = new EventEmitter<any>();
    @Output() onRemove = new EventEmitter<any>();
    @Output() onEdit = new EventEmitter<any>();

    private backupValue: string = '';

    constructor(@Inject('$attrs') private $attrs: any,
                @Inject('$mdUtil') private $mdUtil: any,
                @Inject('$document') private $document: ng.IDocumentService,
                @Inject('$injector') private $injector: ng.IInjectStatic,
                @Inject('$transclude') private $transclude: ng.ITranscludeFunction,
                @Inject('$element') private $element: ng.IAugmentedJQuery,
                @Inject('$window') private $window: ng.IWindowService) {
        'ngInject';
    }

    ngOnInit() {
        if (this.preventMenuClose) {
            this.preventMenuFromClosing();
        }
    }

    handleClick(event) {
        if (this.url) {
            if (event.ctrlKey || event.metaKey || this.openNewTab) {
                this.$window.open(this.url, '_blank');
            } else if (this.url.indexOf('http') > -1) {
                this.$window.location.href = this.url;
            } else {
                this.$injector.get('$location').url(this.url);
            }
        } else {
            this.onClick.emit(event);
        }
    }

    hasIcon() {
        return this.icon || this.$transclude.isSlotFilled('iconsSlot');
    }

    preventMenuFromClosing() {
        angular.element(this.$element[0]).ready(() => {
            const button = this.$element[0].querySelector('.md-button > button');
            if (button) {
                button.attributes.setNamedItem(document.createAttribute('md-prevent-menu-close'));
            }
        })
    }

    attemptFocus(el) {
        if (el && el.getAttribute('tabindex') !== -1) {
            el.focus();
            return (this.$document[0].activeElement === el);
        }
    }

    handleKeyPress($event) {
        let direction;

        if ($event.keyCode === 38 || $event.keyCode === 40) {
            $event.keyCode === 38 ? direction = -1 : direction = 1;

            // since we're in this loop, stop angular material from handling keydown and keyup
            $event.stopPropagation();

            let currentItem = this.$mdUtil.getClosest($event.target, 'MW-MENU-ITEM');
            let items = this.$mdUtil.nodesToArray(currentItem.parentNode.children);

            items = items.filter((item) => {
                return (item.nodeName === 'MW-MENU-ITEM' && item.getAttribute('type') !== 'focusdummy') || item.nodeName === 'MD-MENU-ITEM' || item.nodeName === 'MD-LIST-ITEM';
            });

            let currentIndex = items.indexOf(currentItem);

            // if we hit the bottom of the list and the direction is down, start back at the top
            // similarly if we're at the top of the list and direction is up, start at the bottom
            if (currentIndex === items.length - 1 && direction === 1) {
                currentIndex = -1;
            } else if (currentIndex === 0 && direction === -1) {
                currentIndex = items.length;
            }

            let didFocus;
            for (let i = currentIndex + direction; i >= 0 && i < items.length; i = i + direction) {
                let focusTarget = items[i].querySelector('button.md-button');
                didFocus = this.attemptFocus(focusTarget);
                if (didFocus) {
                    break;
                }
            }

            return didFocus;
        }

        if ($event.keyCode === 13 && this.isEditable && this.editMode) {
            if (this.editModel === '') {
                this.handleCancelClick();
            } else {
                this.editMode = false;
            }
        }

        if ($event.keyCode === 27 && this.isEditable && this.editMode) {
            this.handleCancelClick();
        }
    }

    handleRemoveClick(event) {
        this.onRemove.emit(event);
    }

    handleEditClick(event) {
        this.backupValue = this.editModel;
        if (this.$attrs.onEdit) {
            this.onEdit.emit(event);
        } else {
            this.editMode = true;
        }
    }

    handleCancelClick(restorePrev = true) {
        if (restorePrev) {
            this.editModel = this.backupValue;
        }
        this.editMode = false;
        const input = this.$element[0].querySelector('.mw-menu-item__edit-input input');
        input.blur();
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/menu/menu-item/menu-item.component.ts